/**
 * /u/:id — screen 3t.
 *
 * A person, the way the room thinks about them: their challenge record, what
 * stands between them and you, their pick'em position and how often they show
 * up. `GET /api/users/:id` computes all of it.
 *
 * HEAD TO HEAD only renders when you're looking at somebody else — a card that
 * says "you lead 0–0 against yourself" is noise, and the API returns null for
 * exactly that case.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { PicksCompare } from '@shared/types'
import { teamAbbr, teamName } from '@shared/teams'
import { useStore } from '../lib/store'
import { ApiFailure, GET } from '../lib/api'
import { Avatar, Body, Card, Eyebrow, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'

interface ProfileResponse {
  user: {
    id: string
    displayName: string
    isVerified: boolean
    teamId: string
    secondaryTeamId: string | null
    initials: string
    createdAt: number
    lastSeenAt: number
  }
  challenges: { wins: number; losses: number; pushes: number; streak: number }
  headToHead: { youLead: number; theyLead: number; theyOwe: string[]; youOwe: string[] } | null
  picks: { points: number; rank: number } | null
  attendance: { weeks: number; total: number; streak: number }
}

function ordinal(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}

/** Signed: positive is a win streak, negative a losing one, zero is neither. */
function streakLabel(streak: number): string | null {
  if (streak === 0) return null
  return streak > 0 ? `current streak W${streak}` : `current streak L${Math.abs(streak)}`
}

export default function Profile() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { state } = useStore()

  const [data, setData] = useState<ProfileResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const ac = new AbortController()
    setData(null)
    void (async () => {
      try {
        setData(await GET<ProfileResponse>(`/api/users/${encodeURIComponent(id)}`, ac.signal))
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load that profile.')
      }
    })()
    return () => ac.abort()
  }, [id])

  const isSelf = !!state && !!id && state.user.id === id
  const record = data
    ? `${data.challenges.wins}–${data.challenges.losses}–${data.challenges.pushes}`
    : null
  const streak = data ? streakLabel(data.challenges.streak) : null

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        style={{ padding: '16px 18px 0', paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))' }}
        logo={false}
        left={
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={{ fontSize: 15, fontWeight: 700, minWidth: 44, minHeight: 44, textAlign: 'left' }}
          >
            ←
          </button>
        }
      />

      <Body style={{ padding: '10px 18px calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!data ? (
          <><SkeletonCard lines={2} /><Spacer h={12} /><SkeletonCard lines={3} /><Spacer h={12} /><SkeletonCard lines={3} /></>
        ) : (
          <>
            <div style={{ textAlign: 'center', paddingTop: 10 }}>
              <Avatar
                initials={data.user.initials}
                size={76}
                self
                style={{ margin: '0 auto' }}
              />
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 12, letterSpacing: '-0.02em' }}>
                {data.user.displayName}
                {data.user.isVerified ? <span aria-label="verified"> ✓</span> : null}
              </div>
              <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 4 }}>
                {teamName(data.user.teamId)}
                {data.user.secondaryTeamId ? ` · also ${teamName(data.user.secondaryTeamId)}` : ''}
              </div>
            </div>

            <Eyebrow style={{ marginTop: 26 }}>Challenges</Eyebrow>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>
              {record}
              {streak ? (
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--dim)' }}> · {streak}</span>
              ) : null}
            </div>

            {data.headToHead && !isSelf ? (
              <>
                <Eyebrow style={{ marginTop: 22 }}>Head to head with you</Eyebrow>
                <Card style={{ marginTop: 8, fontSize: 14, lineHeight: 1.9 }}>
                  <div>
                    {data.headToHead.youLead === data.headToHead.theyLead
                      ? `Even at ${data.headToHead.youLead}–${data.headToHead.theyLead}`
                      : data.headToHead.youLead > data.headToHead.theyLead
                        ? `You lead ${data.headToHead.youLead}–${data.headToHead.theyLead}`
                        : `They lead ${data.headToHead.theyLead}–${data.headToHead.youLead}`}
                  </div>
                  <div style={{ color: 'var(--dim)' }}>
                    {data.headToHead.theyOwe.length
                      ? `They owe you ${data.headToHead.theyOwe.join(', ')}`
                      : 'They owe you nothing'}
                  </div>
                  <div style={{ color: 'var(--dim)' }}>
                    {data.headToHead.youOwe.length
                      ? `You owe them ${data.headToHead.youOwe.join(', ')}`
                      : 'You owe them nothing'}
                  </div>
                </Card>
              </>
            ) : null}

            <ProfilePicks userId={data.user.id} name={data.user.displayName} />

            <Eyebrow style={{ marginTop: 22 }}>Pick’em</Eyebrow>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>
              {data.picks ? `${data.picks.points} pts` : 'No picks yet'}
              {data.picks ? (
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--dim)' }}>
                  {' '}· {ordinal(data.picks.rank)} this season
                </span>
              ) : null}
            </div>

            <Eyebrow style={{ marginTop: 22 }}>Attendance</Eyebrow>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>
              {data.attendance.weeks} of {data.attendance.total} weeks
              {data.attendance.streak > 0 ? (
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--dim)' }}>
                  {' '}· {data.attendance.streak}-week streak
                </span>
              ) : null}
            </div>

            {!isSelf ? (
              <Link
                to={`/challenges/new?vs=${encodeURIComponent(data.user.id)}`}
                style={{
                  display: 'block', marginTop: 26, textAlign: 'center', padding: 15,
                  borderRadius: 14, background: 'var(--fill)', color: 'var(--ink)',
                  fontSize: 15, fontWeight: 700, minHeight: 48,
                }}
              >
                Challenge to a feat
              </Link>
            ) : (
              <Link
                to="/settings"
                style={{
                  display: 'block', marginTop: 26, textAlign: 'center', padding: 15,
                  borderRadius: 14, border: '1px solid var(--line)', color: 'var(--fg)',
                  fontSize: 15, fontWeight: 600, minHeight: 48,
                }}
              >
                Edit your profile
              </Link>
            )}
          </>
        )}
      </Body>
    </Screen>
  )
}

// ── their card for the week ahead ───────────────────────────────────────────

/**
 * The same overlay the picks board opens, cut down to a strip.
 *
 * It renders nothing at all when they haven't picked, which is the point: a
 * module reading "0 of 16" on a Tuesday in March is an accusation rather than
 * information. D84 still holds — the endpoint hides an unstarted game's pick,
 * so a card filled in early shows as a count here and nothing more until
 * kickoff.
 */
function ProfilePicks({ userId, name }: { userId: string; name: string }) {
  const [data, setData] = useState<PicksCompare | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    setData(null)
    void (async () => {
      try {
        setData(await GET<PicksCompare>(`/api/picks/u/${encodeURIComponent(userId)}`, ac.signal))
      } catch {
        // A profile without a picks strip is still a profile.
      }
    })()
    return () => ac.abort()
  }, [userId])

  if (!data || data.made === 0) return null

  const visible = data.rows.filter((r) => r.theirPick !== null).slice(0, 8)

  return (
    <>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          gap: 10, marginTop: 22,
        }}
      >
        <Eyebrow>{data.weekLabel} picks</Eyebrow>
        <Link
          to={`/picks/u/${encodeURIComponent(userId)}?week=${encodeURIComponent(data.weekId)}`}
          style={{ fontSize: 12, fontWeight: 700, flex: '0 0 auto' }}
        >
          All {data.total} →
        </Link>
      </div>

      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 8 }}>
        {data.made} of {data.total} picked
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--dim)' }}>
          {' '}· {data.points} {data.points === 1 ? 'point' : 'points'}
        </span>
      </div>

      {visible.length ? (
        <div className="scr" style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 2 }}>
          {visible.map((r) => (
            <div
              key={r.game.id}
              className="mono"
              style={{
                flex: '0 0 auto', padding: '9px 12px', borderRadius: 999, fontSize: 12,
                border: `1px solid ${r.theirCorrect === true ? 'var(--fill)' : 'var(--outline)'}`,
                color: r.theirCorrect === false ? 'var(--dim)' : 'var(--fg)',
              }}
            >
              {teamAbbr(r.theirPick as string)}
              {r.theirCorrect === true ? ' ✓' : r.theirCorrect === false ? ' ✗' : ''}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 8 }}>
          {name}’s picks show up as each game kicks off.
        </div>
      )}
    </>
  )
}
